/**
 * Content Studio API Client
 *
 * API functions for product extraction and social content generation.
 */

import { apiClient, getCsrfToken } from '../api-client';

// ============================================================================
// Types
// ============================================================================

export interface ExtractedProduct {
  title: string;
  description: string | null;
  price: string | null;
  currency: string | null;
  image_url: string | null;
  brand: string | null;
  category: string | null;
  sku: string | null;
  source: string;
  source_url: string;
}

export interface ExtractResponse {
  success: boolean;
  session_id?: string;
  product?: ExtractedProduct;
  error?: string;
  error_code?: string;
}

export interface SocialFormat {
  id: string;
  name: string;
  width: number;
  height: number;
  ratio: string;
  platform: string;
}

export interface CaptionStyle {
  id: string;
  name: string;
  description: string;
}

export interface GeneratedImage {
  format: string;
  s3_key: string;
  width: number;
  height: number;
  image_id: string | null;
  image_url: string | null;  // Presigned S3 URL for display
  caption_style?: string | null;  // Which caption style was used
}

export interface GeneratedCaption {
  caption: string;
  hashtags: string[];
  cta: string;
}

export interface OverlayConfig {
  // Price
  show_price: boolean;

  // Caption
  show_caption: boolean; // NEW: Show caption text on image

  // Hashtags
  show_hashtags: boolean; // NEW: Show hashtags on image

  // CTA (Call-to-Action)
  show_cta: boolean;
  cta_mode: 'auto' | 'manual'; // NEW: Auto-generate or manual input
  cta_text: string; // Manual CTA text

  // USP (Unique Selling Propositions)
  show_usp: boolean;
  usp_mode: 'auto' | 'manual'; // NEW: Auto-generate or manual input
  usp_texts: string[]; // NEW: Multiple USPs (manual mode)
  usp_count: number; // NEW: Number of USPs to generate (1-4, auto mode)
}

export interface GenerateRequest {
  session_id: string;
  formats: string[];
  styles: string[];
  overlay: OverlayConfig;
}

export interface GenerateResponse {
  success: boolean;
  images?: GeneratedImage[];
  captions?: Record<string, GeneratedCaption>;
  error?: string;
}

export interface ContentStudioSession {
  id: string;
  status: 'extracted' | 'generating' | 'completed' | 'failed';
  source_url: string;
  product: ExtractedProduct;
  images: GeneratedImage[];
  captions: Record<string, GeneratedCaption>;
  overlay_config: OverlayConfig;
  error_message: string | null;
  created_at: string;
  updated_at: string;
}

export interface SessionListItem {
  id: string;
  status: string;
  source_url: string;
  product_title: string;
  created_at: string;
}

// ============================================================================
// API Functions
// ============================================================================

/**
 * Extract product data from URL.
 */
export async function extractProduct(url: string): Promise<ExtractResponse> {
  const csrfToken = await getCsrfToken();
  const response = await apiClient.post(
    '/api/v1/content-studio/extract',
    { url },
    { headers: { 'X-CSRFToken': csrfToken } }
  );
  return response.data;
}

/**
 * Extract product from manual upload (fallback for blocked websites).
 */
export async function extractProductManual(
  title: string,
  description: string | null,
  price: string | null,
  currency: string,
  imageFile: File
): Promise<ExtractResponse> {
  const csrfToken = await getCsrfToken();

  // Create FormData for multipart/form-data
  const formData = new FormData();
  formData.append('title', title);
  if (description) formData.append('description', description);
  if (price) formData.append('price', price);
  formData.append('currency', currency);
  formData.append('image', imageFile);

  const response = await apiClient.post(
    '/api/v1/content-studio/extract-manual',
    formData,
    {
      headers: {
        'X-CSRFToken': csrfToken,
        'Content-Type': 'multipart/form-data',
      },
    }
  );
  return response.data;
}

/**
 * Generate social media content.
 */
export async function generateContent(
  request: GenerateRequest
): Promise<GenerateResponse> {
  const csrfToken = await getCsrfToken();
  const response = await apiClient.post(
    '/api/v1/content-studio/generate',
    request,
    { headers: { 'X-CSRFToken': csrfToken } }
  );
  return response.data;
}

/**
 * Get session by ID.
 */
export async function getSession(sessionId: string): Promise<{
  success: boolean;
  session?: ContentStudioSession;
  error?: string;
}> {
  const response = await apiClient.get(
    `/api/v1/content-studio/session/${sessionId}`
  );
  return response.data;
}

/**
 * List recent sessions.
 */
export async function listSessions(
  limit = 20,
  offset = 0
): Promise<{
  success: boolean;
  sessions: SessionListItem[];
}> {
  const response = await apiClient.get('/api/v1/content-studio/sessions', {
    params: { limit, offset },
  });
  return response.data;
}

/**
 * Get available formats.
 */
export async function getFormats(): Promise<{
  formats: SocialFormat[];
}> {
  const response = await apiClient.get('/api/v1/content-studio/formats');
  return response.data;
}

/**
 * Get available caption styles.
 */
export async function getStyles(): Promise<{
  styles: CaptionStyle[];
}> {
  const response = await apiClient.get('/api/v1/content-studio/styles');
  return response.data;
}
